/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import {
  Badge,
  Button,
  FlexColumn,
  FlexRow,
  FormField,
  Input,
  Typography,
} from "design-system";
import {
  EnvelopeIcon,
  SendPlaneIcon,
  TrophyIcon,
} from "design-system/components/Icons";
import LearningShell from "../../components/LearningShell";
import OutcomeFooterNote from "../../components/OutcomeFooterNote";
import { formatLearningScoreSummary } from "../../helpers/mapLearningSubmitResponse";
import { sendLearningCompletionEmail } from "../../helpers/sendLearningCompletionEmail";
import { useLearningQuery } from "../../hooks/useLearningQuery";
import { selectLearningRecipientEmail } from "../../../redux/learning/selectors";

const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i;

export default function SuccessStep({ course, quizResult }) {
  const intl = useIntl();
  const { inviteId, packageId, userId } = useLearningQuery();
  const recipientEmail = useSelector(selectLearningRecipientEmail);
  const resolvedRecipientEmail = quizResult.email || recipientEmail || "";
  const [email, setEmail] = useState(resolvedRecipientEmail);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [sendSuccessMessage, setSendSuccessMessage] = useState(null);

  useEffect(() => {
    if (resolvedRecipientEmail) {
      setEmail(resolvedRecipientEmail);
    }
  }, [resolvedRecipientEmail]);

  const validateEmailBeforeSend = (trimmedEmail) => {
    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      return "please-enter-valid-email-address";
    }

    return null;
  };

  const handleSendEmail = async () => {
    const trimmedEmail = email.trim();
    const resolvedPackageId = packageId || course?.packageId;
    const validationError = validateEmailBeforeSend(trimmedEmail);

    if (validationError) {
      setSendError(validationError);
      return;
    }

    if (!inviteId || !resolvedPackageId || !quizResult.responseId) {
      setSendError("ld-unable-send-email");
      return;
    }

    setSending(true);
    setSendError(null);
    setSendSuccessMessage(null);

    try {
      const result = await sendLearningCompletionEmail({
        inviteId,
        packageId: resolvedPackageId,
        userId,
        responseId: quizResult.responseId,
        email: trimmedEmail,
      });

      if (!result.success) {
        setSendError(result.message || "ld-unable-send-email");
        return;
      }

      setSendSuccessMessage(
        result.message || intl.formatMessage({ id: "ld-email-sent-success" }),
      );
    } catch {
      setSendError("ld-unable-send-email");
    } finally {
      setSending(false);
    }
  };

  const sendErrorMessage = sendError
    ? (sendError.startsWith("ld-") || sendError.startsWith("please-")
      ? intl.formatMessage({ id: sendError })
      : sendError)
    : undefined;

  return (
    <LearningShell
      layout="outcome"
      showWarning={false}
    >
      <FlexColumn align="center" fullWidth className="ld-outcome">
        <FlexColumn
          align="center"
          fullWidth
          gap="var(--ds-space-4)"
          className="ld-outcome__hero"
        >
          <FlexRow
            align="center"
            justify="center"
            fullWidth={false}
            className="ld-outcome__icon ld-outcome__icon--success"
          >
            <TrophyIcon
              size={40}
              className="ld-outcome__icon-glyph"
              aria-hidden
            />
          </FlexRow>

          <Badge variant="positive" emphasis="subtle" size="md" dot>
            {intl.formatMessage({ id: 'ld-completed' })}
          </Badge>

          <Typography
            component="h1"
            variant="headingMedium"
            fontWeight="semibold"
            color="ui-emphasis"
            className="ld-outcome__title"
          >
            {intl.formatMessage({ id: 'ld-success-title' })}
          </Typography>

          <Typography
            component="p"
            variant="bodyMedium"
            color="ui-subtle"
            className="ld-outcome__description"
          >
            {intl.formatMessage(
              { id: 'ld-success-description' },
              { courseTitle: course.title },
            )}
          </Typography>
        </FlexColumn>

        <FlexColumn align="stretch" fullWidth className="ld-surface ld-outcome__panel">
          <FlexRow
            align="center"
            justify="between"
            fullWidth
            className="ld-outcome__panel-row"
          >
            <Typography component="span" variant="bodyMedium" color="ui-subtle">
              {intl.formatMessage({ id: 'ld-score' })}
            </Typography>
            <Typography
              component="span"
              variant="bodyMedium"
              fontWeight="semibold"
              className="ld-outcome__score ld-outcome__score--success"
            >
              {formatLearningScoreSummary(quizResult)}
            </Typography>
          </FlexRow>
          <FlexRow
            align="center"
            justify="between"
            fullWidth
            className="ld-outcome__panel-row"
          >
            <Typography component="span" variant="bodyMedium" color="ui-subtle">
              {intl.formatMessage({ id: 'ld-completed' })}
            </Typography>
            <Typography
              component="span"
              variant="bodyMedium"
              fontWeight="semibold"
              color="ui-emphasis"
            >
              {quizResult.completedAt}
            </Typography>
          </FlexRow>
        </FlexColumn>

        <FlexColumn
          align="stretch"
          fullWidth
          className="ld-surface ld-outcome__panel ld-outcome__email"
        >
          <FlexRow align="end" fullWidth={false} gap="var(--ds-space-2)">
            <EnvelopeIcon
              size={16}
              aria-hidden
              className="ds-icon--color-primary"
            />
            <Typography
              component="h2"
              variant="bodyMedium"
              fontWeight="semibold"
              color="ui-emphasis"
            >
              {intl.formatMessage({ id: 'ld-email-yourself' })}
            </Typography>
          </FlexRow>
          <Typography component="p" variant="bodySmall" color="ui-subtle">
            {intl.formatMessage({ id: 'ld-email-copy-description' })}
          </Typography>
          <FlexRow
            align="stretch"
            fullWidth
            gap="var(--ds-space-3)"
            className="ld-outcome__email-form"
          >
            <FormField
              errorText={sendErrorMessage}
              helperText={sendSuccessMessage || undefined}
              success={Boolean(sendSuccessMessage)}
              className="ld-outcome__email-input"
            >
              <Input
                type="email"
                placeholder={intl.formatMessage({ id: 'ld-email-placeholder' })}
                value={email}
                error={Boolean(sendError)}
                success={Boolean(sendSuccessMessage)}
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (sendError) {
                    setSendError(null);
                  }
                  if (sendSuccessMessage) {
                    setSendSuccessMessage(null);
                  }
                }}
                disabled={sending}
              />
            </FormField>
            <Button
              variant="primary"
              size="base"
              icon={<SendPlaneIcon size={16} />}
              iconPosition="right"
              disabled={sending || !email.trim()}
              onClick={handleSendEmail}
            >
              {sending
                ? intl.formatMessage({ id: "ld-sending-email" })
                : intl.formatMessage({ id: "send" })}
            </Button>
          </FlexRow>
        </FlexColumn>

        <OutcomeFooterNote>
          {intl.formatMessage({ id: 'ld-close-window-safe' })}
        </OutcomeFooterNote>
      </FlexColumn>
    </LearningShell>
  );
}

SuccessStep.propTypes = {
  course: PropTypes.shape({
    description: PropTypes.string,
    logoSrc: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
  quizResult: PropTypes.shape({
    scorePercent: PropTypes.number.isRequired,
    correctCount: PropTypes.number.isRequired,
    totalQuestions: PropTypes.number.isRequired,
    completedAt: PropTypes.string.isRequired,
    email: PropTypes.string,
    responseId: PropTypes.string,
  }).isRequired,
};
